import { Avatar, cn, focusRing, MicOffIcon, PhoneIcon, UiLink } from "@rhema/ui";
import { useIsDesktop } from "../app/media";
import { toneFor } from "../data/view";
import { useClientState } from "../session/session";
import { formatTimer, useCalls, useElapsed } from "./CallProvider";
import { callTitle } from "./people";
import { TrackVideo, tileGround } from "./Tiles";

// The minimised call: a small tile that floats over the app while you read and write chats.
export function FloatingCall() {
  const state = useClientState();
  const { state: calls, snapshot, room, end } = useCalls();
  const desktop = useIsDesktop();
  const active = calls.active;
  const answered = active?.call.answered_at ? Date.parse(active.call.answered_at) : null;
  const elapsed = useElapsed(answered);
  if (!active) return null;

  const remotes = snapshot?.tiles.filter((t) => !t.local) ?? [];
  const focus = remotes.find((t) => t.speaking) ?? remotes.find((t) => t.camera) ?? remotes[0];
  const title = callTitle(state, active.call);
  const tone = toneFor(focus?.identity ?? active.chatId);
  const status = answered
    ? formatTimer(elapsed)
    : snapshot?.status === "connected"
      ? "Ringing"
      : "Connecting";

  return (
    <section
      aria-label="Call in progress"
      className={cn(
        "fixed right-4 z-40 w-[150px] overflow-hidden rounded-[18px] border-[3px] border-linen shadow-float",
        desktop ? "bottom-5" : "bottom-[92px]",
        tileGround[tone],
      )}
    >
      <UiLink
        href={`/calls/${active.chatId}`}
        aria-label={`Return to the call with ${title}`}
        className={cn(
          "relative flex h-[150px] flex-col items-center justify-center gap-2 text-ink",
          focusRing,
        )}
      >
        {focus?.camera ? (
          <TrackVideo track={focus.camera} label={`${focus.name}'s camera`} />
        ) : (
          <Avatar name={focus?.name ?? title} tone={tone} solid serif size={58} />
        )}
        <span className="relative rounded-full bg-linen px-2 py-0.5 text-xs font-semibold">{status}</span>
      </UiLink>
      <div className="flex items-center justify-between gap-2 bg-linen px-2 py-2">
        <button
          type="button"
          aria-label={snapshot?.micOn ? "Mute microphone" : "Unmute microphone"}
          aria-pressed={!snapshot?.micOn}
          onClick={() => void room?.setMic(!snapshot?.micOn)}
          className={cn(
            "flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0",
            snapshot?.micOn ? "bg-surface-header text-ink" : "bg-blue text-white",
            focusRing,
          )}
        >
          <MicOffIcon size={17} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          aria-label="End call"
          onClick={() => void end()}
          className={cn(
            "flex h-9 w-14 cursor-pointer items-center justify-center rounded-full border-0 bg-scarlet text-white",
            focusRing,
          )}
        >
          <PhoneIcon size={18} strokeWidth={2.2} className="rotate-[135deg]" />
        </button>
      </div>
    </section>
  );
}
